/**
 * Session Expired Modal
 * 
 * Displays when a 401 response is received from the API,
 * indicating the user's session has expired.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw, X } from 'lucide-react';

interface SessionExpiredEvent extends CustomEvent {
  detail: {
    message: string;
  };
}

export function SessionExpiredModal() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('Your session has expired. Please log in again.');

  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as SessionExpiredEvent;
      if (customEvent.detail?.message) {
        setMessage(customEvent.detail.message);
      }
      setIsVisible(true);
    };

    window.addEventListener('zkcn:session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('zkcn:session-expired', handleSessionExpired);
    };
  }, []);

  const handleLogin = () => {
    setIsVisible(false);
    navigate('/login');
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-scale-in overflow-hidden">
        {/* Header with warning color */}
        <div className="bg-amber-50 px-6 py-4 border-b border-amber-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <LogOut className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-amber-900">
                Session Expired
              </h2>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-amber-100 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5 text-amber-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-600 mb-6">
            {message}
          </p>

          <div className="flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 btn-secondary py-2.5"
            >
              Dismiss
            </button>
            <button
              onClick={handleLogin}
              className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Log In Again
            </button>
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-6 pb-4">
          <p className="text-xs text-slate-400 text-center">
            Your data is safe. Log in again to continue working.
          </p>
        </div>
      </div>
    </div>
  );
}
